import { autoAvailable } from '@heyjob-dash/shared/fringe-auto/auto-feature';
import { hasTimesheetClockings } from '@heyjob-dash/shared/data/timesheetTypes';
import { MODULE_FLAGS, PREVIEW_FLAGS, isModuleEnabled, isPreviewEnabled } from '@heyjob-dash/shared/config/company-modules';
import { hasSupervisorPermission, supervisorProfileAllows, isExpensesOnlySupervisor } from '@heyjob-dash/shared/utils/check-permission';

import { paths } from 'src/routes/paths';

import Label from 'src/components/label';

import { HighlightDot, HighlightParentDot } from 'src/sections/guides/highlights/highlight-dot';

import Iconify from '../../components/iconify';

const navIcon = (name) => (
  <Iconify sx={{ width: 24, height: 24 }} icon={`solar:${name}-bold-duotone`} />
);

function section(subheader, items) {
  return { subheader, items: items.filter(Boolean) };
}

export function buildCompanyNavV2({
  user,
  t,
  smUp,
  links,
  pendingSignatureCount,
}) {
  const groups = user?.groups ?? [];
  const companyId = user?.data?.current_company;
  const company = user?.data?.companies?.[companyId];
  const module = (flag) => isModuleEnabled(company, flag);
  const preview = (flag) => isPreviewEnabled(company, flag);

  const isCompany = groups.includes('Azienda');
  const canAccessAttendance = groups.some((group) =>
    ['Azienda', 'Responsabile', 'Timesheet'].includes(group),
  );
  const canAccessTimesheet =
    canAccessAttendance &&
    module(MODULE_FLAGS.TIMESHEET) &&
    smUp &&
    (hasSupervisorPermission(user, 'timesheet_view') || groups.includes('Timesheet'));
  const canAccessRequests =
    canAccessAttendance &&
    company?.feature_requests &&
    (hasSupervisorPermission(user, 'requests_view') || groups.includes('Timesheet'));
  const canAccessExpenses =
    hasSupervisorPermission(user, 'expenses_view') && company?.feature_expense_reports;
  const canAccessReports = hasSupervisorPermission(user, 'report_access') && module(MODULE_FLAGS.REPORTS);
  // Il piano ferie legge le richieste: per il Responsabile serve requests_view.
  const canAccessHolidayPlan =
    canAccessAttendance && company?.feature_holiday_plan && supervisorProfileAllows(user, 'requests_view');
  // Responsabile con expenses_config: Configurazione con la sola voce Note spese.
  const canConfigureExpensesOnly =
    !isCompany && hasSupervisorPermission(user, 'expenses_config') && company?.feature_expense_reports;

  const pendingInfo = pendingSignatureCount > 0 ? (
    <Label color="warning" variant="filled">
      {pendingSignatureCount}
    </Label>
  ) : undefined;

  const reportChildren = [
    {
      title: 'Panoramica',
      path: paths.dashboard.reports,
    },
    module(MODULE_FLAGS.TIMESHEET) && company?.timesheet_types?.some(hasTimesheetClockings) && {
      title: 'Timbrature',
      path: paths.dashboard.clockings,
    },
    module(MODULE_FLAGS.TIMESHEET) && {
      title: t('timesheet'),
      path: paths.dashboard.stats,
    },
    module(MODULE_FLAGS.TIMESHEET) && {
      title: 'Verifica cartellini',
      path: paths.dashboard.timesheetCheckReport,
    },
    isCompany && smUp && module(MODULE_FLAGS.PAYROLL_DATA) && {
      title: 'Dati contabili',
      path: paths.dashboard.payrollData,
    },
    isCompany && smUp && preview(PREVIEW_FLAGS.LABOR_COST_BUDGET) && {
      title: 'Budget costo lavoro',
      path: paths.dashboard.laborCostBudget,
    },
    (isCompany || groups.includes('Responsabile')) && smUp && preview(PREVIEW_FLAGS.EMPLOYEES_RESIDUALS) && {
      title: 'Residui dipendenti',
      path: paths.dashboard.employeesPayrollData,
    },
    isCompany && smUp && {
      title: 'Utenti',
      path: paths.dashboard.employeesSecurityReport,
    },
    isCompany && smUp && module(MODULE_FLAGS.DOCUMENTS) && {
      title: 'Accesso documenti',
      path: paths.dashboard.employeesDocuments,
    },
    (isCompany || groups.includes('Responsabile')) && smUp && preview(PREVIEW_FLAGS.CUSTOM_REPORT) && {
      title: 'Personalizzato',
      path: paths.dashboard.employeesCustomReport,
    },
    isCompany && preview(PREVIEW_FLAGS.PAY_TRANSPARENCY) && {
      title: 'Trasparenza retributiva',
      path: paths.dashboard.payTransparency,
      info: <HighlightDot id="trasparenza-retributiva" />,
    },
  ].filter(Boolean);

  const configChildren = [
    smUp && {
      title: t('general'),
      path: paths.dashboard.config.root,
    },
    {
      title: t('custom_fields'),
      path: paths.dashboard.config.customFields,
    },
    smUp && module(MODULE_FLAGS.TIMESHEET) && {
      title: t('timesheet'),
      path: paths.dashboard.config.timesheet,
    },
    smUp && module(MODULE_FLAGS.TIMESHEET) && company?.clocking_mode === 'qr' && {
      title: t('clocking places'),
      path: paths.dashboard.config.clockingPlaces,
    },
    // Solo le aziende che timbrano da file esterno hanno un connettore da seguire.
    company?.clocking_mode === 'external' && {
      title: 'Connettore timbrature',
      path: paths.dashboard.config.clockingConnector,
    },
    company?.feature_expense_reports && {
      title: t('expense_reports'),
      path: paths.dashboard.config.expenseReports,
    },
    {
      title: t('supervisors'),
      path: paths.dashboard.config.supervisors,
    },
    preview(PREVIEW_FLAGS.ORG_CHART) && {
      title: 'Organigramma',
      path: paths.dashboard.config.orgChart,
    },
    smUp && module(MODULE_FLAGS.DOCUMENTS) && module(MODULE_FLAGS.DOCUMENT_GENERATION) && {
      title: t('document_templates'),
      path: paths.dashboard.config.documentTemplates,
    },
    company?.feature_holiday_plan && {
      title: t('holiday_plan'),
      path: paths.dashboard.config.holidayPlan,
    },
    module(MODULE_FLAGS.PROJECTS) && {
      title: t('projects'),
      path: paths.dashboard.config.projects,
    },
    preview(PREVIEW_FLAGS.CONNECT_API) && {
      title: t('connect_api'),
      path: paths.dashboard.connectApi,
    },
  ].filter(Boolean);

  const attendanceChildren = [
    canAccessTimesheet && {
      title: 'Cartellini',
      path: paths.dashboard.timesheet,
    },
    canAccessRequests && {
      title: t('requests'),
      path: paths.dashboard.requests,
    },
    canAccessHolidayPlan && {
      title: <span style={{ textTransform: 'none' }}>{t('holiday_plan')}</span>,
      path: paths.dashboard.holidayPlan,
    },
  ].filter(Boolean);

  const documentChildren = [
    isCompany && module(MODULE_FLAGS.DOCUMENTS) && {
      title: 'Documenti azienda',
      path: paths.dashboard.documents,
    },
    isCompany && module(MODULE_FLAGS.DOCUMENTS) && (module(MODULE_FLAGS.DOCUMENT_GENERATION) || module(MODULE_FLAGS.SIGNATURES)) && {
      title: <span style={{ textTransform: 'none' }}>Invii e firme</span>,
      path: paths.dashboard.documentSignatures,
      info: (
        <>
          {pendingInfo}
          <HighlightDot id="invii-firme" />
        </>
      ),
    },
    // Su richiesta: lo studio lo attiva con «Caricamento buste paga e dichiarazioni annuali».
    isCompany && module(MODULE_FLAGS.DOCUMENTS) && company?.allows_company_upload_payslips === true && {
      title: 'Upload buste paga',
      path: paths.dashboard.uploadBustePaga,
    },
  ].filter(Boolean);

  const communicationChildren = [
    isCompany && smUp && module(MODULE_FLAGS.COMMUNICATIONS) && {
      title: t('notices'),
      path: paths.dashboard.notices,
    },
    isCompany && module(MODULE_FLAGS.COMMUNICATIONS) && {
      title: t('letters'),
      path: paths.dashboard.letters,
    },
  ].filter(Boolean);

  const supportChildren = [
    isCompany && smUp && {
      title: t('help_desk'),
      path: paths.dashboard.helpDesk,
    },
    isCompany && {
      title: t('product_updates.label'),
      path: paths.dashboard.news.root,
    },
    {
      title: t('faq'),
      path: paths.dashboard.faq,
    },
    smUp && isCompany && links.length > 0 && {
      title: t('external_services'),
      path: '#/external',
      children: links.map((link) => ({
        title: link.description,
        path: link.url.startsWith('http') ? link.url : `https://${link.url}`,
        externalLink: true,
      })),
    },
  ].filter(Boolean);

  return [
    section('Panoramica', [
      !isExpensesOnlySupervisor(user) && {
        title: t('dashboard'),
        path: paths.dashboard.root,
        icon: navIcon('home-2'),
      },
      (isCompany || hasSupervisorPermission(user, 'registry_access')) && {
        title: t('employees'),
        path: paths.dashboard.employees,
        icon: navIcon('users-group-rounded'),
      },
    ]),
    section('Operatività', [
      attendanceChildren.length > 0 && {
        title: t('attendance'),
        path: attendanceChildren[0].path,
        icon: navIcon('calendar-mark'),
        children: attendanceChildren,
      },
      hasSupervisorPermission(user, 'scadenzario_access') && module(MODULE_FLAGS.DEADLINES) && {
        title: t('timetable'),
        path: paths.dashboard.calendar,
        icon: navIcon('calendar'),
      },
      canAccessExpenses && {
        title: t('expense_reports'),
        path: paths.dashboard.expenseReports,
        icon: navIcon('wallet-money'),
      },
      autoAvailable(user, ['Azienda']) && { title: 'Auto', path: paths.dashboard.auto, icon: navIcon('wheel') },
      documentChildren.length > 0 && {
        title: t('documents'),
        path: documentChildren[0].path,
        icon: navIcon('folder-with-files'),
        info: (
          <>
            {pendingInfo}
            {isCompany && <HighlightParentDot ids={['invii-firme']} />}
          </>
        ),
        badge: isCompany ? <HighlightParentDot ids={['invii-firme']} floating /> : undefined,
        children: documentChildren,
      },
      communicationChildren.length > 0 && {
        title: t('notices'),
        path: communicationChildren[0].path,
        icon: navIcon('chat-round-dots'),
        children: communicationChildren,
      },
      canAccessReports && {
        title: 'Report',
        path: paths.dashboard.reports,
        info: isCompany && preview(PREVIEW_FLAGS.PAY_TRANSPARENCY) ? <HighlightParentDot ids={['trasparenza-retributiva']} /> : undefined,
        badge: isCompany && preview(PREVIEW_FLAGS.PAY_TRANSPARENCY)
          ? <HighlightParentDot ids={['trasparenza-retributiva']} floating />
          : undefined,
        icon: navIcon('chart-square'),
        children: reportChildren,
      },
    ]),
    section('Sistema', [
      isCompany && {
        title: t('config'),
        path: paths.dashboard.config.root,
        icon: navIcon('settings'),
        children: [
          ...configChildren,
          {
            title: t('your company'),
            path: paths.dashboard.company,
          },
        ],
      },
      canConfigureExpensesOnly && {
        title: t('config'),
        path: paths.dashboard.config.expenseReports,
        icon: navIcon('settings'),
        children: [
          {
            title: t('expense_reports'),
            path: paths.dashboard.config.expenseReports,
          },
        ],
      },
      {
        title: 'Supporto',
        path: supportChildren[0].path,
        icon: navIcon('help'),
        children: supportChildren,
      },
    ]),
  ].filter((group) => group.items.length > 0);
}
